import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { StreemApiExpertTradeArea } from '@streem/api';
import { HostCallLogDetails } from '@streem/sdk-react';
import { ToastTypes } from '@streem/ui-react';

import { CompanySettingsStore } from '../stores/company_settings_store';
import { useGlobalStore } from './use_global_context';
import { CurrentCallSourceStore } from '../stores/current_call_source_store';
import { AuthStore } from '../stores/auth_store';
import { SDKStore } from '../stores/sdk_store';
import { useGetCompanyStore } from './detail_store_hooks';
import { useSurveys } from './use_surveys';
import { useGroupsStore } from './use_groups_store';
import { useHoursOfOperation } from './use_hours_of_operation';
import { NoteTemplatesStore } from '../stores/note_templates_store';

interface InCallListenersInputs {
    companySettingsStore: CompanySettingsStore;
    history: ReturnType<typeof useHistory>;
    currentCallSourceStore: CurrentCallSourceStore;
    authStore: AuthStore;
    sdkStore: SDKStore;
    noteTemplatesStore: NoteTemplatesStore;
    handleAnswerCallError: () => void;
    handleAnswerCallCanceled: () => void;
    onAnswerCallback: () => Promise<void>;
    getRoomCallLogHistory: () => Promise<HostCallLogDetails[] | undefined>;
    companyStore: ReturnType<typeof useGetCompanyStore>;
    groupStore: ReturnType<typeof useGroupsStore>;
    displaySurveys: ReturnType<typeof useSurveys>;
    hoursOfOperation: StreemApiExpertTradeArea[];
    isVideoProviderVerboseLoggingEnabled: boolean;
}
export function useInCallListener(): InCallListenersInputs {
    const {
        sdkStore,
        authStore,
        companySettingsStore,
        uiStore,
        currentCallSourceStore,
        noteTemplatesStore,
        userStore,
    } = useGlobalStore();
    const history = useHistory();
    const displaySurveys = useSurveys();
    const companyStore = useGetCompanyStore();
    const groupStore = useGroupsStore();

    const getRoomCallLogHistory = useCallback(() => {
        return sdkStore.getRoomCallLogHistory();
    }, [sdkStore]);

    const handleAnswerCallError = useCallback(() => {
        uiStore.addToastWithManualClose({
            content: 'Error connecting to video chat. Please check your connection.',
            flavor: ToastTypes.ERROR,
            id: 'answer-call-failed',
        });
    }, [uiStore]);

    const handleAnswerCallCanceled = useCallback(() => {
        uiStore.addToast({
            flavor: ToastTypes.WARNING,
            id: `incoming-call-canceled-by-customer`,
            content: 'Your incoming call was canceled by the customer.',
        });
    }, [uiStore]);

    const onAnswerCallback = useCallback(async () => {
        // increment the answers called
        userStore.onCallAnswered();
    }, [userStore]);

    const hoursOfOperation = useHoursOfOperation(companySettingsStore);

    const isVideoProviderVerboseLoggingEnabled =
        companySettingsStore.videoProviderVerboseLogging
            ?.split(',')
            .map(id => id.trim())
            .includes(authStore.userId) ?? false;

    return {
        companySettingsStore,
        history,
        currentCallSourceStore,
        authStore,
        sdkStore,
        noteTemplatesStore,
        handleAnswerCallError,
        handleAnswerCallCanceled,
        onAnswerCallback,
        getRoomCallLogHistory,
        companyStore,
        groupStore,
        displaySurveys,
        hoursOfOperation,
        isVideoProviderVerboseLoggingEnabled,
    };
}
